import { BLOCKED_PRICE_IMPACT_NON_EXPERT } from '../constants'
import { CurrencyAmount, Fraction, JSBI, Percent, TokenAmount, Trade, Token } from '@uniswap/sdk'
import { ALLOWED_PRICE_IMPACT_HIGH, ALLOWED_PRICE_IMPACT_LOW, ALLOWED_PRICE_IMPACT_MEDIUM } from '../constants'
import { Field } from '../state/swap/actions'
import { basisPointsToPercent } from './index'
import { isTaxToken, getTotalBuyTax, getTotalSellTax } from './dynamicTaxToken'

const BASE_FEE = new Percent(JSBI.BigInt(30), JSBI.BigInt(10000))
const ONE_HUNDRED_PERCENT = new Percent(JSBI.BigInt(10000), JSBI.BigInt(10000))
const INPUT_FRACTION_AFTER_FEE = ONE_HUNDRED_PERCENT.subtract(BASE_FEE)

// computes price breakdown for the trade
export function computeTradePriceBreakdown(
  trade?: Trade
): { priceImpactWithoutFee?: Percent; realizedLPFee?: CurrencyAmount; taxFee?: Percent } {
  // for each hop in our trade, take away the x*y=k price impact from 0.3% fees
  // e.g. for 3 tokens/2 hops: 1 - ((1 - .03) * (1-.03))
  const realizedLPFee = !trade
    ? undefined
    : ONE_HUNDRED_PERCENT.subtract(
        trade.route.pairs.reduce<Fraction>(
          (currentFee: Fraction): Fraction => currentFee.multiply(INPUT_FRACTION_AFTER_FEE),
          ONE_HUNDRED_PERCENT
        )
      )

  // Calculate tax fees for tax tokens
  let taxFee: Percent | undefined = undefined
  if (trade) {
    const inputToken = trade.inputAmount.currency instanceof Token ? trade.inputAmount.currency : undefined
    const outputToken = trade.outputAmount.currency instanceof Token ? trade.outputAmount.currency : undefined

    let totalTaxPercent = JSBI.BigInt(0)

    // Add sell tax if input is tax token
    if (inputToken && isTaxToken(inputToken)) {
      const sellTax = getTotalSellTax(inputToken)
      totalTaxPercent = JSBI.add(totalTaxPercent, sellTax.numerator)
    }

    // Add buy tax if output is tax token
    if (outputToken && isTaxToken(outputToken)) {
      const buyTax = getTotalBuyTax(outputToken)
      totalTaxPercent = JSBI.add(totalTaxPercent, buyTax.numerator)
    }

    if (JSBI.greaterThan(totalTaxPercent, JSBI.BigInt(0))) {
      taxFee = new Percent(totalTaxPercent, JSBI.BigInt(10000))
    }
  }

  // remove lp fees and tax fees from price impact
  let adjustedPriceImpact = trade?.priceImpact
  if (adjustedPriceImpact && realizedLPFee) {
    adjustedPriceImpact = adjustedPriceImpact.subtract(realizedLPFee)
  }
  if (adjustedPriceImpact && taxFee) {
    adjustedPriceImpact = adjustedPriceImpact.subtract(taxFee)
  }

  // the x*y=k impact
  const priceImpactWithoutFeePercent = adjustedPriceImpact
    ? new Percent(adjustedPriceImpact.numerator, adjustedPriceImpact.denominator)
    : undefined

  // the amount of the input that accrues to LPs
  const realizedLPFeeAmount =
    realizedLPFee &&
    trade &&
    (trade.inputAmount instanceof TokenAmount
      ? new TokenAmount(trade.inputAmount.token, realizedLPFee.multiply(trade.inputAmount.raw).quotient)
      : CurrencyAmount.ether(realizedLPFee.multiply(trade.inputAmount.raw).quotient))

  return { priceImpactWithoutFee: priceImpactWithoutFeePercent, realizedLPFee: realizedLPFeeAmount, taxFee }
}

// computes the minimum amount out and maximum amount in for a trade given a user specified allowed slippage in bips
export function computeSlippageAdjustedAmounts(
  trade: Trade | undefined,
  allowedSlippage: number
): { [field in Field]?: CurrencyAmount } {
  const pct = basisPointsToPercent(allowedSlippage)
  
  if (!trade) {
    return {
      [Field.INPUT]: undefined,
      [Field.OUTPUT]: undefined
    }
  }

  const inputAmount = trade.maximumAmountIn(pct)
  let outputAmount = trade.minimumAmountOut(pct)

  // For tax tokens, we'll let the increased slippage handle the difference
  // Don't double-adjust here since we already increased slippage in the swap page

  return {
    [Field.INPUT]: inputAmount,
    [Field.OUTPUT]: outputAmount
  }
}

export function warningSeverity(priceImpact: Percent | undefined): 0 | 1 | 2 | 3 | 4 {
  if (!priceImpact?.lessThan(BLOCKED_PRICE_IMPACT_NON_EXPERT)) return 4
  if (!priceImpact?.lessThan(ALLOWED_PRICE_IMPACT_HIGH)) return 3
  if (!priceImpact?.lessThan(ALLOWED_PRICE_IMPACT_MEDIUM)) return 2
  if (!priceImpact?.lessThan(ALLOWED_PRICE_IMPACT_LOW)) return 1
  return 0
}

export function formatExecutionPrice(trade?: Trade, inverted?: boolean): string {
  if (!trade) {
    return ''
  }
  return inverted
    ? `${trade.executionPrice.invert().toSignificant(6)} ${trade.inputAmount.currency.symbol} / ${
        trade.outputAmount.currency.symbol
      }`
    : `${trade.executionPrice.toSignificant(6)} ${trade.outputAmount.currency.symbol} / ${
        trade.inputAmount.currency.symbol
      }`
}
